import type { FC, ReactNode } from 'react'

import {
    ArrowUpRight,
    BookOpen,
    Inbox,
    LifeBuoy,
    Mail
} from 'lucide-react'
import { Layout } from '@/components/generals/layout'
import { Seo } from '@/components/generals/seo'
import { FinalCta } from '@/components/kit/final-cta'
import { PageLink } from '@/components/kit/page-link'
import { Reveal } from '@/components/kit/reveal'
import { SectionLabel } from '@/components/kit/section-label'
import { SplitText } from '@/components/kit/split-text'
import { FaqSection } from '@/components/home/home-faq'
import { SupportForm } from '@/components/contact/support-form'
import { contactFaqs, supportSteps } from '@/constants/help'
import { SIGNUP_HREF, START_LABEL, siteConfig } from '@/constants/site'

const selfHelp = [
    { href: '/help', title: 'Centro de ayuda', body: 'Respuestas rápidas sobre el uso de Himelio.', icon: LifeBuoy },
    { href: '/docs', title: 'Documentación', body: 'Guías paso a paso de cada módulo.', icon: BookOpen }
]

const Servicios: FC = (): ReactNode => {
    return (
        <Layout>
            <Seo
                title='Contacto y soporte'
                description='Escribe al equipo de Himelio para soporte técnico, ventas y planes. Correo oficial servicios@himelio.com y tickets desde esta página.'
            />

            <section
                aria-labelledby='servicios-title'
                className='relative overflow-hidden pb-14 pt-36 md:pt-44'
            >
                <div
                    aria-hidden='true'
                    className='bg-grid mask-radial absolute inset-0'
                />

                <div className='relative mx-auto max-w-[1400px] px-5 md:px-10'>
                    <SectionLabel>Contacto y soporte</SectionLabel>
                    <SplitText
                        as='h1'
                        id='servicios-title'
                        lines={['Hablemos de', 'tu negocio']}
                        italic='negocio'
                        className='mt-6 max-w-[14ch] text-[clamp(3rem,8.4vw,7.75rem)] font-bold leading-[0.88] tracking-[-0.045em] md:max-w-none'
                    />

                    <Reveal
                        delay={150}
                        className='mt-8 flex flex-col gap-5 md:flex-row md:items-end md:justify-between'
                    >
                        <p className='max-w-xl text-lg leading-relaxed text-stone md:text-xl'>
                            Contacta al equipo de Himelio para soporte técnico, ventas y planes. Las respuestas se gestionan por correo y por nuestro sistema de tickets, así cada solicitud tiene seguimiento.
                        </p>
                        <span className='stamp w-fit -rotate-3 text-ember'>Respuesta por correo</span>
                    </Reveal>

                    <div className='mt-14 grid gap-6 lg:grid-cols-12 lg:gap-8'>
                        <Reveal
                            delay={200}
                            className='lg:col-span-7'
                        >
                            <SupportForm />
                        </Reveal>

                        <Reveal
                            delay={300}
                            className='flex flex-col gap-4 lg:col-span-5'
                        >
                            <div className='rounded-[2rem] border border-ink/10 bg-paper p-7 md:p-8'>
                                <p className='font-mono text-[11px] uppercase tracking-[0.16em] text-stone'>Correo oficial de soporte</p>
                                <a
                                    href={`mailto:${siteConfig.email}`}
                                    className='group mt-6 flex items-center gap-4'
                                >
                                    <span className='flex size-11 shrink-0 items-center justify-center rounded-full bg-sand transition-colors group-hover:bg-ember group-hover:text-cream'>
                                        <Mail className='size-5' />
                                    </span>
                                    <span className='min-w-0'>
                                        <span className='block text-sm text-stone'>Escríbenos a</span>
                                        <span className='block break-words text-lg font-semibold underline decoration-ink/20 underline-offset-4 transition-colors group-hover:decoration-ember'>{siteConfig.email}</span>
                                    </span>
                                </a>
                                <p className='mt-5 text-sm leading-relaxed text-stone'>
                                    Para soporte técnico, ventas y planes, y temas de facturación o de tu cuenta.
                                </p>
                            </div>

                            <div className='flex items-start gap-4 rounded-[2rem] border border-ink/10 bg-paper p-7 md:p-8'>
                                <span className='relative flex size-12 shrink-0 items-center justify-center rounded-full bg-ink text-cream'>
                                    <Inbox className='size-5' />
                                    <span className='absolute -right-0.5 -top-0.5 size-3 animate-pulse-dot rounded-full bg-live' />
                                </span>
                                <div>
                                    <p className='font-heading text-2xl font-bold tracking-[-0.04em]'>Respondemos por correo</p>
                                    <p className='mt-1 text-sm leading-relaxed text-stone'>
                                        Cada solicitud se registra como un ticket. Así el equipo sigue tu caso de principio a fin y la respuesta llega a tu bandeja de entrada.
                                    </p>
                                </div>
                            </div>

                            <div className='rounded-[2rem] border border-ink/10 bg-paper p-7 md:p-8'>
                                <p className='font-mono text-[11px] uppercase tracking-[0.16em] text-stone'>Antes de escribir</p>
                                <ul className='mt-5 flex flex-col gap-2'>
                                    {selfHelp.map(item => {
                                        const Icon = item.icon

                                        return (
                                            <li key={item.href}>
                                                <PageLink
                                                    href={item.href}
                                                    className='group flex items-center gap-4 rounded-[1.25rem] p-3 -mx-3 transition-colors hover:bg-sand'
                                                >
                                                    <span className='flex size-11 shrink-0 items-center justify-center rounded-full bg-sand transition-colors group-hover:bg-ember group-hover:text-cream'>
                                                        <Icon className='size-5' />
                                                    </span>
                                                    <span className='min-w-0 flex-1'>
                                                        <span className='block font-semibold'>{item.title}</span>
                                                        <span className='block text-sm text-stone'>{item.body}</span>
                                                    </span>
                                                    <ArrowUpRight className='size-5 shrink-0 text-stone transition-transform duration-500 ease-expo group-hover:rotate-45 group-hover:text-ink' />
                                                </PageLink>
                                            </li>
                                        )
                                    })}
                                </ul>
                            </div>

                            <a
                                href={SIGNUP_HREF}
                                className='group grain relative flex flex-1 flex-col justify-between gap-10 overflow-hidden rounded-[2rem] theme-light bg-paper p-7 text-ink [--grain-opacity:0.18] md:p-8'
                            >
                                <span className='font-mono text-[11px] uppercase tracking-[0.16em] text-ink/75'>¿Aún no tienes cuenta?</span>
                                <span className='flex items-end justify-between gap-6'>
                                    <span>
                                        <span className='block font-heading text-[2rem] font-bold leading-[0.95] tracking-[-0.035em]'>
                                            {START_LABEL}
                                        </span>
                                        <span className='mt-2 block text-sm text-ink/75'>Plan Pro completo, sin tarjeta de crédito.</span>
                                    </span>
                                    <span className='flex size-12 shrink-0 items-center justify-center rounded-full bg-ink text-cream transition-transform duration-500 ease-expo group-hover:rotate-45'>
                                        <ArrowUpRight className='size-5' />
                                    </span>
                                </span>
                            </a>
                        </Reveal>
                    </div>
                </div>
            </section>

            <section
                aria-labelledby='next-steps-title'
                className='mx-auto max-w-[1400px] px-5 py-20 md:px-10 md:py-28'
            >
                <div className='rule-double mb-12' />
                <SectionLabel>Qué pasa después</SectionLabel>
                <SplitText
                    as='h2'
                    id='next-steps-title'
                    lines={['Así atendemos', 'tu solicitud']}
                    italic='solicitud'
                    className='mt-5 text-[clamp(2.5rem,5.6vw,5.25rem)] font-bold leading-[0.9] tracking-[-0.045em]'
                />
                <ol className='mt-12 grid gap-5 md:grid-cols-3'>
                    {supportSteps.map((step, index) => (
                        <Reveal
                            as='li'
                            key={step.title}
                            delay={index * 100}
                            settle={[-1.2, 0.8, -0.6][index]}
                        >
                            <div className='relative h-full rounded-[1.75rem] border border-ink/10 bg-paper p-7'>
                                <span
                                    aria-hidden='true'
                                    className='tape -top-3 left-8 rotate-2'
                                />
                                <p className='font-mono text-xs uppercase tracking-[0.16em] text-ember'>{String(index + 1).padStart(2, '0')} · {step.stamp}</p>
                                <h3 className='mt-4 text-3xl font-bold tracking-[-0.04em]'>{step.title}</h3>
                                <p className='mt-3 text-base leading-relaxed text-stone'>{step.body}</p>
                            </div>
                        </Reveal>
                    ))}
                </ol>
            </section>

            <FaqSection
                items={contactFaqs}
                id='contacto-preguntas'
                lines={['Antes de', 'escribirnos']}
            />

            <FinalCta />
        </Layout>
    )
}

export default Servicios
